import React from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styled from "styled-components/macro";

import { Row, HorizontalScrollBox } from "components/Layout";

import TabSelector from ".";

const Wrapper = styled.div(
  ({ theme }) => `
  margin: 18px 0 0 0;
  border-bottom: 1px solid #c8c8c8;
  padding: 0 ${theme.space[3]}px;
  @media (max-width: ${theme.breakpoints[0]}px) {
    padding: 0 ${theme.space[1]}px;
  }
`,
);

const IconTab = styled(FontAwesomeIcon)`
  font-size: 1rem;
  position: relative;
  top: -1px;
`;

interface Tab {
  id: string;
  title: string;
  icon?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic tab component props
  component?: React.ComponentType<any>;
  display?: boolean;
  onClick?: () => void;
}

interface DefaultTabProps extends Omit<Tab, "component" | "display"> {
  bold?: boolean;
  color?: string;
  disabled?: boolean;
  selectedTab: string;
  setSelectedTab: (id: string) => void;
}

export const DefaultTab = ({
  bold,
  color,
  disabled,
  id,
  onClick,
  selectedTab,
  setSelectedTab,
  title,
  icon,
}: DefaultTabProps) => (
  <TabSelector
    data-tour={id}
    selected={id === selectedTab}
    onClick={
      typeof onClick === "function"
        ? onClick
        : () => !disabled && setSelectedTab(id)
    }
    bold={bold}
    color={color}
  >
    {title || id} {icon && <IconTab icon={icon} />}
  </TabSelector>
);

interface TabRowProps {
  selectedTab: string;
  tabs: Array<Tab>;
  setSelectedTab?: (id: string) => void;
  bold?: boolean;
  color?: string;
  style?: React.CSSProperties;
  backgroundColor?: string;
}

const TabRow = ({
  selectedTab,
  tabs,
  setSelectedTab,
  bold,
  color,
  style,
  backgroundColor = "#fff",
}: TabRowProps) => {
  const tabElements = tabs.map(tab => {
    const { component: TabComponent = DefaultTab, ...tabProps } = tab;
    return (
      <React.Fragment key={tab.id}>
        {tab.display === true || typeof tab.display === "undefined" ? (
          <TabComponent
            {...tabProps}
            bold={bold}
            color={color}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            icon={tab.icon ? tab.icon : null}
          />
        ) : null}
      </React.Fragment>
    );
  });

  if (tabs.length === 0) {
    return null;
  }

  return (
    <Wrapper data-tour={`tabrow.${selectedTab}`} style={style}>
      <HorizontalScrollBox backgroundColor={backgroundColor}>
        <Row>{tabElements}</Row>
      </HorizontalScrollBox>
    </Wrapper>
  );
};

export default React.memo(TabRow);
